import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { SalesOrderDto } from "@grizzly/shared";
import { salesOrderStatus } from "@grizzly/shared";
import { api } from "../lib/apiClient.js";
import type { ApiClientError } from "../lib/apiClient.js";
import { formatAmount, formatDateTime } from "../lib/format.js";
import { useI18n, type MessageKey } from "../i18n/index.js";
import { Badge } from "../components/ui/badge.js";
import { Button } from "../components/ui/button.js";
import { PageHeader } from "../components/ui/card.js";
import { DataTable, type Column } from "../components/ui/dataTable.js";
import { Modal } from "../components/ui/modal.js";
import { SelectInput } from "../components/ui/field.js";
import { useToast } from "../lib/useToast.js";
import { Wrench } from "lucide-react";

function defaultDueDate(): string {
  const d = new Date();
  d.setDate(d.getDate() + 14);
  return d.toISOString().slice(0, 10);
}

export function SalesOrdersPage() {
  const { push } = useToast();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [items, setItems] = useState<SalesOrderDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [convertTarget, setConvertTarget] = useState<SalesOrderDto | null>(null);
  const [dueDate, setDueDate] = useState(defaultDueDate());
  const [priority, setPriority] = useState("medium");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.listSalesOrders();
      setItems(res.items);
      setError(null);
    } catch (err) {
      setError((err as ApiClientError).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const convert = useCallback(async () => {
    if (!convertTarget) return;
    setBusy(true);
    try {
      const res = await api.convertSalesOrder(convertTarget.id, priority, dueDate);
      push(
        t("sales.jobsCreated"),
        t("sales.jobsCreatedDesc", {
          count: res.jobs.length,
          order: convertTarget.number,
        })
      );
      setConvertTarget(null);
      navigate("/jobs");
    } catch (err) {
      const e = err as ApiClientError;
      push(e.message, undefined, "danger");
    } finally {
      setBusy(false);
    }
  }, [convertTarget, dueDate, navigate, priority, push, t]);

  const columns: Column<SalesOrderDto>[] = [
    {
      key: "number",
      header: t("sales.order"),
      render: (r) => <span className="tabular font-medium text-ink">{r.number}</span>,
    },
    {
      key: "customer",
      header: t("common.fields.customer"),
      render: (r) => r.customerName,
    },
    {
      key: "status",
      header: t("common.status"),
      render: (r) => (
        <Badge tone={salesOrderStatus(r.status).tone}>
          {t(`status.salesOrder.${r.status}` as MessageKey)}
        </Badge>
      ),
    },
    {
      key: "total",
      header: t("common.fields.total"),
      render: (r) => <span className="tabular">{formatAmount(r.totalMinor)}</span>,
    },
    {
      key: "when",
      header: t("sales.ordered"),
      render: (r) => formatDateTime(r.orderedAt),
    },
    {
      key: "lines",
      header: t("sales.lines"),
      render: (r) => (
        <span className="text-xs text-ink-muted">
          {r.lines.map((l) => l.description).join(", ") || "-"}
        </span>
      ),
    },
    {
      key: "actions",
      header: t("common.actions"),
      render: (r) =>
        r.status === "open" ? (
          <Button
            size="sm"
            variant="secondary"
            disabled={busy}
            onClick={() => setConvertTarget(r)}
          >
            <Wrench className="h-4 w-4" aria-hidden="true" /> {t("sales.createJobs")}
          </Button>
        ) : null,
    },
  ];

  return (
    <>
      <PageHeader
        title={t("sales.salesOrders")}
        description={t("sales.salesOrdersDescription")}
      />
      {error ? (
        <p role="alert" className="mb-4 text-sm text-action-danger">
          {error}
        </p>
      ) : null}
      <DataTable
        caption={t("sales.salesOrders")}
        loading={loading}
        rows={items}
        rowKey={(r) => r.id}
        columns={columns}
        emptyTitle={t("sales.emptyTitle")}
        emptyDescription={t("sales.emptyDescription")}
      />

      <Modal
        open={convertTarget !== null}
        onOpenChange={(open) => {
          if (!open) setConvertTarget(null);
        }}
        title={t("sales.createJobsFrom", {
          order: convertTarget?.number ?? t("sales.order"),
        })}
        description={t("sales.createJobsDescription")}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConvertTarget(null)}>
              {t("common.cancel")}
            </Button>
            <Button variant="primary" onClick={convert} disabled={busy || !dueDate}>
              {busy ? t("common.creating") : t("sales.createJobs")}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="convert-due" className="text-sm font-medium text-ink">
              {t("sales.targetDate")}
            </label>
            <input
              id="convert-due"
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="h-11 rounded-[var(--radius-card)] border border-line bg-surface-raised px-3 text-sm"
            />
          </div>
          <SelectInput
            label={t("common.fields.priority")}
            options={[
              { value: "low", label: t("status.priority.low") },
              { value: "medium", label: t("status.priority.medium") },
              { value: "high", label: t("status.priority.high") },
            ]}
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
          />
        </div>
      </Modal>
    </>
  );
}
